import { LitElement, unsafeCSS, CSSResult } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { literal, html } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import styles from './button.scss?inline';
import slotStyles from './button.slot.scss?inline';
import { appendStylesheet, c11nElement } from '../core';
import { C11nSpinnerLiteral } from '../spinner';
import '../spinner';


export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonVariant = 'primary' | 'secondary';

@c11nElement()
export class ButtonComponent extends LitElement {
  static selector = `${C11nPrefix}-button`;
  static literal = literal`${C11nPrefixLiteral}-button`;
  static override styles: CSSResult = unsafeCSS(styles);

  shakeAnimationLength: number = 300;
  isButton: boolean = false;

  @queryAssignedElements()
  _slottedEl!: Array<HTMLElement>;

  // Some attributes (size, variant) were being stripped in Stencil apps,
  // but we found a simple workaround. If using @property 
  // we may need to differentiate the name from the attribute 
  // https://github.com/ionic-team/stencil/issues/2703

  @property({ attribute: 'invalid', type: Boolean })
  _invalid?: boolean;

  @property({ attribute: 'pending', type: Boolean })
  _pending?: boolean;

  @property({ type: Boolean, reflect: true })
  shake: boolean = false;
  
  @property({ attribute: 'size', type: String })
  _size: ButtonSize = 'medium';

  @property({ attribute: 'variant', type: String, converter: (value: string | null) => value ?? 'primary' })
  _variant: ButtonVariant = 'primary';

  @property({ attribute: 'c11n-web-button', type: Boolean, reflect: true })
  readonly _c11n_web_button: boolean = true;

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();
    
    setTimeout(() => {
      this.handlePending();
    });
  }

  appendStyles() {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-button\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
  }

  clickHandler() {
    if (this._invalid) {
      this.doShake();
    }
  }

  doShake() {
    this.shake = true;
    setTimeout(() => this.shake = false, this.shakeAnimationLength);
  }

  handlePending() {
    const slottedElement = this._slottedEl[0] as HTMLButtonElement | HTMLLinkElement;
    if (slottedElement.nodeName === 'BUTTON') {
      if (this._pending) {
        slottedElement.setAttribute('disabled', 'true');
      } else {
        slottedElement.removeAttribute('disabled');
      }
    }
  }
  
  getButtonTemplate() {
    return html`<slot @click="${this.clickHandler}"></slot>`; // https://stackoverflow.com/a/60445957
  }

  getPendingTemplate() {
    return html`
      <span class="c11n-button__wrapper">
        <${C11nSpinnerLiteral} size="small" part="spinner"></${C11nSpinnerLiteral}>
        <slot @click="${this.clickHandler}"></slot>
      </span>
    `; // https://stackoverflow.com/a/60445957
  }

  override render() {
    return html`${ this._pending ? this.getPendingTemplate() : this.getButtonTemplate() }`;
  }
}

export const C11nButton = ButtonComponent.selector;
export const C11nButtonLiteral = ButtonComponent.literal;
